import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { XamaLogo, LogOut, CampaignHistoryChat } from '../../assets/svgs/icons';
import SidebarCollapsedRail from './SidebarCollapsedRail';
import SidebarNavigation from './SidebarNavigation';
import SidebarUtilityActions from './SidebarUtilityActions';
import './SidebarHistoricoCampanhas.css';
import upgrade from '../../assets/logos/upgrade.svg';
import ArtLimitProgress from '../ArtLimitProgress';
import { MIN_ARTS_TO_CREATE_CAMPAIGN } from '../../constants/campaignCreation';
import { useAuth } from '../../hooks/useAuth';
import { useClient } from '../../hooks/useClient';

const PUBLISHED_STATUSES = [
  'publicada',
  'publicadas',
  'publicado',
  'publicados',
  'published',
  'posted',
];

const SCHEDULED_STATUSES = [
  'agendada',
  'agendadas',
  'agendado',
  'agendados',
  'scheduled',
];

const COMPLETED_CAMPAIGN_STATUSES = [
  'completed',
  'finalizada',
  'finalizado',
  'finished',
];

const isCampaignHistoryPage = (pathname) => {
  const path = String(pathname || '').toLowerCase()

  return (
    path === '/'
    || path === '/chatsocket'
    || path === '/campaign/chat'
    || path === '/agent/chat'
  )
};

const getNumericValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const getExpectedArtsCount = (campaign = {}) => {
  const directCandidates = [
    campaign?.totalPieces,
    campaign?.totalArts,
    campaign?.totalImages,
    campaign?.artCount,
    campaign?.piecesCount,
    campaign?.expectedPublications,
    campaign?.strategyResult?.totalPieces,
    campaign?.strategyResult?.totalArts,
    campaign?.strategyResult?.totalImages,
  ];

  for (const candidate of directCandidates) {
    const resolved = getNumericValue(candidate);
    if (resolved) {
      return resolved;
    }
  }

  const arrayCandidates = [
    campaign?.arts,
    campaign?.results,
    campaign?.images,
    campaign?.strategyResult?.arts,
    campaign?.strategyResult?.results,
    campaign?.strategyResult?.strategyHeadlinesAndIdentity?.pieces,
    campaign?.strategyResult?.artDirector?.pieces_execution,
  ];

  for (const candidate of arrayCandidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate.length;
    }
  }

  return null;
};

const SidebarHistoricoCampanhas = ({
  onSelect,
  selecionada,
  campaings,
  onLoadMore,
  hasMore,
  isLoading,
  onSearch,
  onCreate,
  isExpanded,
  onToggleExpanded,
  artesCriadas = 0,
  limiteArtes = 0,
  availableArts = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout, userData } = useAuth();
  const location = useLocation();
  const { clientData } = useClient();

  const STATUS_CONFIG = {
    waiting: { label: t('campaign_history_sidebar.status.waiting'), tone: 'waiting' },
    in_progress: { label: t('campaign_history_sidebar.status.in_progress'), tone: 'progress' },
    scheduled: { label: t('campaign_history_sidebar.status.scheduled'), tone: 'scheduled' },
    completed: { label: t('campaign_history_sidebar.status.completed'), tone: 'completed' },
    not_finished: { label: t('campaign_history_sidebar.status.not_finished'), tone: 'not-finished' },
  };

  const [expandidoInterno, setExpandidoInterno] = useState(true);
  const expandido = typeof isExpanded === 'boolean' ? isExpanded : expandidoInterno;
  const listRef = useRef(null);
  const loadingRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isHistoryOpen, setIsHistoryOpen] = useState(() =>
    isCampaignHistoryPage(window.location.pathname),
  );

  useEffect(() => {
    setIsHistoryOpen(isCampaignHistoryPage(location.pathname));
  }, [location.pathname]);

  const getAvailableArtsValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  };

  const availableArtsValue = getAvailableArtsValue(availableArts);
  const isCampaignCreationBlocked =
    availableArtsValue === null ||
    availableArtsValue < MIN_ARTS_TO_CREATE_CAMPAIGN;

  const userDisplayName = [userData?.name, userData?.surname]
    .filter(Boolean)
    .join(' ')
    .trim() || userData?.email || t('campaign_history_sidebar.default_user_name');

  const userCompanyName =
    clientData?.companyName ||
    userData?.userAccount?.[0]?.fantasyName ||
    userData?.userAccount?.[0]?.corporateName ||
    '';

  const userInitial = userDisplayName.charAt(0).toUpperCase();

  const handleToggleExpandido = () => {
    if (typeof onToggleExpanded === 'function') {
      onToggleExpanded((prev) => !prev);
      return;
    }

    setExpandidoInterno((prev) => !prev);
  };

  const handleCreateConversation = () => {
    if (isCampaignCreationBlocked) {
      return;
    }

    onCreate?.();
  };

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    if (typeof onSearch === 'function') {
      onSearch(debouncedQuery);
    }
  }, [debouncedQuery, onSearch]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const onScroll = () => {
      if (!hasMore || isLoading) return;
      if (loadingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;
      const threshold = 120;
      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        loadingRef.current = true;
        Promise.resolve(onLoadMore && onLoadMore()).finally(() => {
          setTimeout(() => {
            loadingRef.current = false;
          }, 300);
        });
      }
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [onLoadMore, hasMore, isLoading]);

  const getStatusMeta = (campaign) => {
    const publications = Array.isArray(campaign?.publications)
      ? campaign.publications
      : [];

    const normalizedCampaignStatus = String(campaign?.status || '')
      .trim()
      .toLowerCase();

    if (publications.length === 0) {
      if (COMPLETED_CAMPAIGN_STATUSES.includes(normalizedCampaignStatus)) {
        return STATUS_CONFIG.completed;
      }

      return STATUS_CONFIG.not_finished;
    }

    const normalizedStatuses = publications
      .map((publication) => String(publication?.status || '').trim().toLowerCase());

    const isPublishedStatus = (status) =>
      PUBLISHED_STATUSES.includes(status);

    const isScheduledStatus = (status) =>
      SCHEDULED_STATUSES.includes(status);

    const expectedArtsCount = Math.max(
      getExpectedArtsCount(campaign) || 0,
      publications.length,
    );

    const publishedCount = normalizedStatuses.filter(isPublishedStatus).length;
    const scheduledOrPublishedCount = normalizedStatuses.filter(
      (status) => isScheduledStatus(status) || isPublishedStatus(status),
    ).length;
    const allPublished = expectedArtsCount > 0 && publishedCount >= expectedArtsCount;

    if (allPublished) {
      return STATUS_CONFIG.scheduled;
    }

    if (publishedCount > 0) {
      return STATUS_CONFIG.in_progress;
    }

    if (scheduledOrPublishedCount > 0) {
      return STATUS_CONFIG.waiting;
    }

    if (COMPLETED_CAMPAIGN_STATUSES.includes(normalizedCampaignStatus)) {
      return STATUS_CONFIG.completed;
    }

    return STATUS_CONFIG.not_finished;
  };

  return (
    <aside className={`sidebar-historico-campanhas${expandido ? '' : ' retraido'}`}>
      <div className="sidebar-expanded-view" aria-hidden={!expandido}>
        <div className="sidebar-header">
          <div className="sidebar-header-brand">
            <Link to="/ChatSocket" className="sidebar-logo-link" aria-label="Xamã">
              <XamaLogo width={100} height={28} color="#000024" />
            </Link>
            <button
              className="sidebar-collapse-btn"
              onClick={handleToggleExpandido}
              aria-label={t('campaign_history_sidebar.collapse_menu')}
              title={t('campaign_history_sidebar.collapse_menu')}
              type="button"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="1.5" y="2.5" width="15" height="13" rx="2" stroke="#414141" strokeWidth="1.2" />
                <path d="M6.5 2.5V15.5" stroke="#414141" strokeWidth="1.2" />
              </svg>
            </button>
          </div>
        </div>

        <div className="sidebar-content">
            <div className="sidebar-content-main">
              <button
                type="button"
                className={`sidebar-new-conversation-btn${isCampaignCreationBlocked ? ' disabled' : ''}`}
                onClick={handleCreateConversation}
                disabled={isCampaignCreationBlocked}
                title={
                  isCampaignCreationBlocked
                    ? t('campaign_history_sidebar.insufficient_arts_tooltip', { min: MIN_ARTS_TO_CREATE_CAMPAIGN })
                    : ''
                }
              >
                {t('campaign_history_sidebar.new_conversation')}
              </button>

              <ArtLimitProgress
                artesCriadas={artesCriadas}
                limiteArtes={limiteArtes}
                layout="sidebar"
              />
            </div>

            <div className="sidebar-nav-scroll">
              <SidebarNavigation />
            </div>

            <div className="sidebar-content-body">
              <div className="sidebar-history-section">
              <button
                className="sidebar-history-header"
                onClick={() => setIsHistoryOpen((prev) => !prev)}
                type="button"
                aria-expanded={isHistoryOpen}
              >
                <span className="sidebar-history-title">{t('campaign_history_sidebar.section_title')}</span>
                <span className={`sidebar-chevron ${isHistoryOpen ? 'sidebar-chevron-open' : ''}`}>
                  <svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M7.37629 7.37629C7.18876 7.56376 6.93445 7.66907 6.66929 7.66907C6.40412 7.66907 6.14982 7.56376 5.96229 7.37629L0.305288 1.71929C0.209778 1.62704 0.133596 1.5167 0.0811869 1.39469C0.0287779 1.27269 0.00119157 1.14147 3.77564e-05 1.00869C-0.00111606 0.87591 0.0241859 0.744231 0.0744668 0.621335C0.124748 0.498438 0.199001 0.386786 0.292893 0.292893C0.386786 0.199 0.498438 0.124747 0.621334 0.0744663C0.74423 0.0241854 0.87591 -0.00111606 1.00869 3.77571e-05C1.14147 0.00119157 1.27269 0.0287779 1.39469 0.0811869C1.5167 0.133596 1.62704 0.209778 1.71929 0.305288L6.66929 5.25529L11.6193 0.305288C11.8079 0.12313 12.0605 0.0223355 12.3227 0.0246139C12.5849 0.0268924 12.8357 0.132061 13.0211 0.317469C13.2065 0.502877 13.3117 0.75369 13.314 1.01589C13.3162 1.27808 13.2154 1.53069 13.0333 1.71929L7.37629 7.37629Z" fill="#414141" fillOpacity="0.7" />
                  </svg>
                </span>
              </button>

              <div
                className={`sidebar-history-collapse${isHistoryOpen ? ' sidebar-history-collapse-open' : ''}`}
                aria-hidden={!isHistoryOpen}
              >
                <div className="sidebar-history-content">
                  <div className="sidebar-search-container">
                    <input
                      aria-label={t('campaign_history_sidebar.search_aria')}
                      placeholder={t('campaign_history_sidebar.search_placeholder')}
                      className="sidebar-search-input"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <div className="sidebar-search-icon">
                      <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20.75 22.5L12.875 14.625C12.25 15.125 11.5313 15.5208 10.7188 15.8125C9.90625 16.1042 9.04167 16.25 8.125 16.25C5.85417 16.25 3.9325 15.4633 2.36 13.89C0.787501 12.3167 0.000833995 10.395 6.61376e-07 8.125C-0.000832672 5.855 0.785834 3.93333 2.36 2.36C3.93417 0.786667 5.85583 0 8.125 0C10.3942 0 12.3163 0.786667 13.8913 2.36C15.4663 3.93333 16.2525 5.855 16.25 8.125C16.25 9.04167 16.1042 9.90625 15.8125 10.7188C15.5208 11.5312 15.125 12.25 14.625 12.875L22.5 20.75L20.75 22.5ZM8.125 13.75C9.6875 13.75 11.0158 13.2033 12.11 12.11C13.2042 11.0167 13.7508 9.68833 13.75 8.125C13.7492 6.56167 13.2025 5.23375 12.11 4.14125C11.0175 3.04875 9.68917 2.50167 8.125 2.5C6.56083 2.49833 5.23292 3.04542 4.14125 4.14125C3.04958 5.23708 2.5025 6.565 2.5 8.125C2.4975 9.685 3.04458 11.0133 4.14125 12.11C5.23792 13.2067 6.56583 13.7533 8.125 13.75Z" fill="#878787" />
                      </svg>
                    </div>
                    {searchQuery && (
                      <button
                        aria-label={t('campaign_history_sidebar.clear_search')}
                        className="sidebar-search-clear"
                        onClick={() => setSearchQuery('')}
                        type="button"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  <div ref={listRef} className="sidebar-scroll-container">
                    <ul className="sidebar-list">
                      {campaings?.map((camp) => {
                        const statusMeta = getStatusMeta(camp);
                        const isSelected = selecionada === (camp?.campaignArtGenerationId || camp?.uuid || camp?.id);

                        return (
                          <li
                            key={camp?.uuid || camp?.campaignArtGenerationId || camp?.id}
                            className={`sidebar-item${isSelected ? ' selecionada' : ''}`}
                            onClick={() => onSelect && onSelect(camp?.campaignArtGenerationId || camp?.uuid || camp?.id)}
                          >
                            <span className="sidebar-item-chat-icon" aria-hidden="true">
                              <CampaignHistoryChat />
                            </span>
                            <div className="sidebar-info">
                              <div className="sidebar-camp-header-row">
                                <span className="sidebar-camp-title">{camp?.campaignName || t('campaign_history_sidebar.new_campaign')}</span>
                                <span className={`sidebar-status-badge sidebar-status-${statusMeta.tone}`}>
                                  {statusMeta.label}
                                </span>
                              </div>
                            </div>
                            {isSelected && <span className="sidebar-item-active-dot" aria-hidden="true" />}
                          </li>
                        );
                      })}
                    </ul>
                    {isLoading && (
                      <div className="sidebar-loading-indicator">{t('campaign_history_sidebar.loading')}</div>
                    )}
                    <div className="sidebar-list-fade" />
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>

          <div className="sidebar-content-footer">
            <button
              className="sidebar-upgrade-btn"
              onClick={() => navigate('/PlanCheckoutUser')}
              type="button"
            >
              <img src={upgrade} alt="" className="sidebar-upgrade-icon" />
              <span className="sidebar-upgrade-label">{t('campaign_history_sidebar.upgrade_plan')}</span>
            </button>

            <div className="sidebar-user-footer">
              <div className="sidebar-user-avatar">{userInitial}</div>
              <div className="sidebar-user-info">
                <span className="sidebar-user-name">{userDisplayName}</span>
                {userCompanyName ? (
                  <span className="sidebar-user-company">{userCompanyName}</span>
                ) : null}
              </div>
              <div className="sidebar-user-actions">
                <SidebarUtilityActions menuPlacement="top" />
                <button
                  type="button"
                  className="sidebar-logout-btn"
                  onClick={logout}
                  aria-label={t('log_out.button_log_out')}
                >
                  <LogOut width={20} height={20} color="rgba(65, 65, 65, 0.73)" />
                </button>
              </div>
            </div>
          </div>
      </div>

      <SidebarCollapsedRail
        onToggleExpanded={handleToggleExpandido}
        onCreate={handleCreateConversation}
        isCampaignCreationBlocked={isCampaignCreationBlocked}
        artesCriadas={artesCriadas}
        limiteArtes={limiteArtes}
        userInitial={userInitial}
        onLogout={logout}
      />
      </aside>
  );
};

export default SidebarHistoricoCampanhas;
